import { useRef, useState } from "react";
import type { MouseEvent, PointerEvent as ReactPointerEvent } from "react";

/** 끌기로 보는 최소 이동(px) — 그보다 작으면 누르기(고르기) */
const DRAG_PX = 6;

/**
 * 학생 칩 끌어 놓기(2026-10-11 · 편성판 · 릴레이 구간판) — 포인터 이벤트로 직접 한다.
 * 예전 HTML5 끌어 놓기(draggable)는 칩이 &lt;button&gt; 이라 Firefox 에서 끌리지 않고, 태블릿 터치에서도 대부분 동작하지 않았다
 * (그래서 '고른 뒤 ＋' 만 됐다). 마우스 · 터치 · 펜 모두 같은 길: 누른 채 6px 넘게 움직이면 끌기 → 손가락을 따라가는 이름표 →
 * 놓은 자리 아래의 칸(`data-drop`)에 넣는다. 끌고 난 뒤 따라오는 click 은 삼킨다(고르기로 치지 않는다) · 그냥 누르면 예전처럼 고르기.
 */
export function usePointerDrag<T extends string | number>(
  onDrop: (pid: string, target: T) => void,
) {
  const [drag, setDrag] = useState<{ pid: string; label: string; x: number; y: number } | null>(
    null,
  );
  const [over, setOver] = useState<T | null>(null);
  const start = useRef<{
    pid: string;
    label: string;
    x0: number;
    y0: number;
    id: number;
    moved: boolean;
  } | null>(null);
  const swallow = useRef(false);
  const dropRef = useRef(onDrop);
  dropRef.current = onDrop;

  /** 그 화면 자리 아래의 칸(가장 가까운 data-drop 조상) */
  const targetAt = (x: number, y: number): T | null => {
    const el = document.elementFromPoint?.(x, y)?.closest?.("[data-drop]");
    const raw = el?.getAttribute("data-drop");
    if (raw == null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  };
  const end = () => {
    start.current = null;
    setDrag(null);
    setOver(null);
  };

  /** 끌 수 있는 칩에 붙인다 */
  const source = (pid: string, label: string) => ({
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      start.current = { pid, label, x0: e.clientX, y0: e.clientY, id: e.pointerId, moved: false };
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const s = start.current;
      if (!s || s.id !== e.pointerId) return;
      if (!s.moved) {
        if (Math.hypot(e.clientX - s.x0, e.clientY - s.y0) < DRAG_PX) return;
        s.moved = true;
        try {
          e.currentTarget.setPointerCapture?.(e.pointerId);
        } catch {
          /* 합성 이벤트 — 캡처 없이도 이어진다 */
        }
      }
      setDrag({ pid: s.pid, label: s.label, x: e.clientX, y: e.clientY });
      setOver(targetAt(e.clientX, e.clientY));
    },
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
      const s = start.current;
      if (!s || s.id !== e.pointerId) return;
      const moved = s.moved;
      const t = moved ? targetAt(e.clientX, e.clientY) : null;
      end();
      if (!moved) return;
      swallow.current = true;
      if (t !== null) dropRef.current(s.pid, t);
    },
    onPointerCancel: end,
    onClickCapture: (e: MouseEvent<HTMLElement>) => {
      if (!swallow.current) return;
      swallow.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
    "data-dragging": drag?.pid === pid ? "true" : undefined,
    // 터치로 끌 때 화면이 스크롤되지 않게(칩 위에서만)
    style: { touchAction: "none" } as const,
  });

  /** 놓을 수 있는 칸에 붙인다 */
  const target = (t: T) => ({ "data-drop": JSON.stringify(t) });

  /** 손가락을 따라가는 이름표(끄는 동안만) */
  const ghost = drag ? (
    <span
      className="pf-drag-ghost"
      style={{ left: drag.x, top: drag.y }}
      aria-hidden="true"
      data-testid="drag-ghost"
    >
      {drag.label}
    </span>
  ) : null;

  return { source, target, over, ghost, dragging: drag?.pid ?? null };
}

export type DragSource = ReturnType<ReturnType<typeof usePointerDrag>["source"]>;
